import type { ShareToChatSubject } from "@xop/dashboard-shared"

import type { DetailedCustomer } from "../types/customer"
import type { DetailedLead } from "../types/lead"

/** Company name, falling back to the contact's full name, then a placeholder. */
function entityTitle(opts: {
  company_name?: string | null
  first_name?: string | null
  last_name?: string | null
  full_name?: string | null
}): string {
  if (opts.company_name?.trim()) return opts.company_name.trim()
  if (opts.full_name?.trim()) return opts.full_name.trim()
  const person = `${opts.first_name ?? ""} ${opts.last_name ?? ""}`.trim()
  return person || "Unknown"
}

/**
 * "Share to chat" subject for a CRM prospect (regular OR AI — they share the
 * `/crm/prospects/:id` detail route). Summary is industry + status; the deep
 * link is matched by the chat applink allowlist.
 */
export function prospectShareSubject(prospect: DetailedLead): ShareToChatSubject {
  return {
    kind: "Prospect",
    title: entityTitle(prospect),
    summaryLines: [prospect.industry ?? "", prospect.status ? `Status: ${prospect.status}` : ""],
    deepLinkPath: `/crm/prospects/${prospect.id}`,
  }
}

/**
 * "Share to chat" subject for a CRM customer.
 *
 * NON-MONETARY WALL: the summary deliberately carries only industry + status —
 * never contract value / MRR / ARR / lifetime value. Sharing a customer into
 * chat must not leak money fields, same discipline as the CRM → Support
 * boundary (see CLAUDE.md + docs/crm.md).
 */
export function customerShareSubject(customer: DetailedCustomer): ShareToChatSubject {
  return {
    kind: "Customer",
    title: entityTitle(customer),
    summaryLines: [customer.industry ?? "", customer.status ? `Status: ${customer.status}` : ""],
    deepLinkPath: `/crm/customers/${customer.id}`,
  }
}
